// MyPreferences — donor portal preferences page (CHG-1513 portal-pivot retarget).
//
// β.6 of the donor-portal program plan; retargeted in CHG-1513 from the
// /api/v1/me/preferences shim to portal.py canonical /api/v1/my-giving/preferences
// (DonorCommunicationPreferences schema).
//
// CHG-2945 (Slice I, donor-portal self-service remediation — DP-R8) brings
// this page to v1 parity:
//   - EXPLICIT per-org scope. The backend PATCH without org_id fans out to
//     ALL linked donor profiles; v1 (CommunicationPreferencesSection /
//     PreferencesTab) forces per-org selection. Pre-CHG-2945 this page
//     silently wrote all-org — opposite semantics on the same endpoint.
//     Now an organization picker (sourced from GET /my-giving/organizations,
//     first org preselected, mirroring v1's CHG-0108b behavior) scopes every
//     read and write, and the copy states the scope before save.
//   - display_preference control (CHG-2885 / DRIFT-1122) with the exact v1
//     labels (web/dashboard …/CommunicationPreferencesSection.tsx
//     DISPLAY_PREFERENCE_OPTIONS — keep in sync by string).
//   - preferred_channel options corrected to the backend enum
//     (email/phone/mail/none) — the previous static:preferred_channel
//     options_source had no lookup entry, so the select rendered no
//     usable options.
//   - Transactional-vs-marketing clarity copy (same wording as the v1
//     surfaces — keep in sync by string).
//
// LOST vs CHG-1498 shim:
//   - locale (now lives on /my-giving/me profile per portal AccountUpdate)
//   - intent_topics (no portal equivalent today; future BE extension)

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsPage } from "../components/composition";
import type { SettingsSection } from "../lib/settings-sections";
import {
  fetchMePreferences,
  fetchMyOrganizations,
  patchMePreferences,
  type MePreferences,
  type MePreferencesPatch,
  type MyPreferenceOrg,
} from "../api/me-preferences";

const SECTION_ID = "preferences";

// CHG-2945 — synthetic scope field id; never part of the PATCH payload.
const ORG_SCOPE_FIELD = "org_scope";

// CHG-2885 display_preference labels — MUST match v1's
// DISPLAY_PREFERENCE_OPTIONS (CommunicationPreferencesSection.tsx) verbatim.
export const DISPLAY_PREFERENCE_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "org_default", label: "Organization default" },
  { value: "full_name", label: "Full name" },
  { value: "first_last_initial", label: "First name + last initial" },
  { value: "anonymous", label: "Anonymous" },
];

// Backend PreferredChannel enum (models/donor.py) with v1's labels.
const PREFERRED_CHANNEL_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone / SMS" },
  { value: "mail", label: "Postal Mail" },
  { value: "none", label: "No Contact" },
];

// CHG-2945 shared clarity copy — IDENTICAL wording on the v1 surfaces
// (CommunicationPreferencesSection.tsx + my-org-detail/PreferencesTab.tsx).
// Scope sentence: rendered before save so the donor can tell which orgs a
// save affects (program exit criterion).
function scopeSentence(orgName: string): string {
  return `Saving applies to ${orgName} only — your preferences for other organizations are not changed.`;
}

// Transactional-vs-marketing: the channel opt-ins govern marketing-style
// outreach; transactional messages are a different class. Security emails
// bypass the consent check at the sender (skip_consent_check); receipts are
// always issued + portal-visible, and the emailed copy follows the email
// channel consent (EmailSenderService.check_donor_consent). Copy is written
// to that ACTUAL behavior — do not claim emailed receipts ignore the email
// toggle.
const TRANSACTIONAL_NOTE =
  "Donation receipts and account-security messages are transactional, not marketing. " +
  "Security messages are always delivered, and receipts are always issued and kept in your giving history. " +
  "If you turn email off, marketing email stops and receipt copies are no longer emailed — your receipts remain available in the portal.";

const DISPLAY_PREFERENCE_HINT =
  "Controls how your name is shown on this organization's donor walls and live giving feeds. " +
  '"Organization default" follows the org\'s own display policy. ' +
  '"Anonymous" hides your name on public displays only — it does not anonymize your donation history, which the organization keeps for receipts and records.';

function buildSection(
  orgs: MyPreferenceOrg[],
  selectedOrgName: string | undefined,
): SettingsSection {
  return {
    section_id: SECTION_ID,
    label: "Preferences",
    icon: "settings",
    description: TRANSACTIONAL_NOTE,
    route: "?section=preferences",
    field_groups: [
      {
        group_id: "scope",
        label: "Organization",
        fields: [
          {
            field_id: ORG_SCOPE_FIELD,
            label: "Organization",
            field_type: "select",
            pending_primitive: true,
            required: true,
            options: orgs.map((o) => ({ value: o.id, label: o.name })),
            hint: selectedOrgName
              ? scopeSentence(selectedOrgName)
              : "Select an organization to view and edit its preferences.",
          },
        ],
      },
      {
        group_id: "channels",
        label: "How we can reach you",
        fields: [
          {
            field_id: "email_opt_in",
            label: "Email",
            field_type: "toggle",
            hint: "Receive updates, newsletters, and campaign news from this organization by email.",
          },
          {
            field_id: "sms_opt_in",
            label: "SMS",
            field_type: "toggle",
            hint: "Receive donation confirmations and reminders via text message.",
          },
          {
            field_id: "push_opt_in",
            label: "Push",
            field_type: "toggle",
            hint: "Receive app push notifications. Your consent is saved now; push delivery is not yet active.",
          },
          {
            field_id: "postal_opt_in",
            label: "Postal mail",
            field_type: "toggle",
            hint: "Allow this organization to reach you by postal mail. Your consent is saved now; postal sending is not yet active.",
          },
        ],
      },
      {
        group_id: "preferred",
        label: "Preferred channel",
        fields: [
          {
            field_id: "preferred_channel",
            label: "Preferred contact method",
            field_type: "select",
            pending_primitive: true,
            options: PREFERRED_CHANNEL_OPTIONS,
            hint: "Your preferred contact method for this organization. This is a routing hint only — it does not override the per-channel choices above.",
          },
        ],
      },
      {
        group_id: "annual",
        label: "Annual report",
        fields: [
          {
            field_id: "annual_report_opt_in",
            label: "Receive annual reports",
            field_type: "toggle",
            hint: "Consent to be listed and acknowledged in this organization's annual reports.",
          },
        ],
      },
      {
        group_id: "privacy",
        label: "How your name appears",
        fields: [
          {
            field_id: "display_preference",
            label: "How your name appears",
            field_type: "select",
            pending_primitive: true,
            options: DISPLAY_PREFERENCE_OPTIONS,
            hint: DISPLAY_PREFERENCE_HINT,
          },
        ],
      },
    ],
  };
}

function mePreferencesToValues(p: MePreferences): Record<string, unknown> {
  return {
    email_opt_in: p.email_opt_in,
    sms_opt_in: p.sms_opt_in,
    push_opt_in: p.push_opt_in,
    postal_opt_in: p.postal_opt_in,
    preferred_channel: p.preferred_channel,
    annual_report_opt_in: p.annual_report_opt_in,
    display_preference: p.display_preference ?? "org_default",
  };
}

function buildPatch(
  server: Record<string, unknown>,
  edits: Record<string, unknown>,
): MePreferencesPatch {
  const patch: MePreferencesPatch = {};
  const writable = [
    "email_opt_in",
    "sms_opt_in",
    "push_opt_in",
    "postal_opt_in",
    "preferred_channel",
    "annual_report_opt_in",
    "display_preference",
  ] as const;
  for (const key of writable) {
    if (!(key in edits)) continue;
    if (!Object.is(edits[key], server[key])) {
      // Index signature workaround — keys are typed by the writable tuple.
      (patch as Record<string, unknown>)[key] = edits[key];
    }
  }
  return patch;
}

export function MyPreferences() {
  const queryClient = useQueryClient();

  // CHG-2945 — org-scope picker source. Same universe as v1's
  // donorProfiles: orgs where the donor holds a linked supporter profile.
  const { data: orgs, error: orgsError } = useQuery({
    queryKey: ["me", "organizations"],
    queryFn: fetchMyOrganizations,
  });

  // Preselect the first org so the form loads without an extra click
  // (mirrors v1 CHG-0108b). Multi-org donors switch via the picker.
  const [selectedOrgId, setSelectedOrgId] = useState("");
  useEffect(() => {
    if (!selectedOrgId && orgs && orgs.length > 0) {
      setSelectedOrgId(orgs[0].id);
    }
  }, [orgs, selectedOrgId]);

  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ["me", "preferences", selectedOrgId],
    queryFn: () => fetchMePreferences(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const [edits, setEdits] = useState<Record<string, unknown>>({});

  const serverValues = useMemo<Record<string, unknown>>(
    () => (data ? mePreferencesToValues(data) : {}),
    [data],
  );
  const values = useMemo<Record<string, unknown>>(
    () => ({
      ...serverValues,
      ...edits,
      [ORG_SCOPE_FIELD]: selectedOrgId,
    }),
    [serverValues, edits, selectedOrgId],
  );

  const dirty = useMemo(() => {
    return Object.keys(buildPatch(serverValues, edits)).length > 0;
  }, [edits, serverValues]);

  const mutation = useMutation({
    mutationFn: (patch: MePreferencesPatch) =>
      patchMePreferences(patch, selectedOrgId),
    onSuccess: () => {
      setEdits({});
      queryClient.invalidateQueries({
        queryKey: ["me", "preferences", selectedOrgId],
      });
    },
  });

  const onChange = (_sectionId: string, fieldId: string, value: unknown) => {
    if (fieldId === ORG_SCOPE_FIELD) {
      // Scope switch, not a preference edit: drop unsaved edits (the new
      // org's server values load fresh — same semantics as v1's per-org
      // query keyed on the selector).
      if (typeof value === "string" && value) {
        setSelectedOrgId(value);
        setEdits({});
      }
      return;
    }
    setEdits((prev) => ({ ...prev, [fieldId]: value }));
  };

  const onSaveSection = async (_sectionId: string) => {
    if (!selectedOrgId) return; // explicit scope required before save
    const patch = buildPatch(serverValues, edits);
    if (Object.keys(patch).length === 0) return;
    mutation.mutate(patch);
  };

  const selectedOrgName = useMemo(
    () => orgs?.find((o) => o.id === selectedOrgId)?.name,
    [orgs, selectedOrgId],
  );

  const section = useMemo(
    () => buildSection(orgs ?? [], selectedOrgName),
    [orgs, selectedOrgName],
  );

  const error = mutation.error
    ? extractErrorMessage(mutation.error)
    : fetchError
    ? extractErrorMessage(fetchError)
    : orgsError
    ? extractErrorMessage(orgsError)
    : undefined;

  return (
    <SettingsPage
      title="My preferences"
      sections={[section]}
      values={{ [SECTION_ID]: values }}
      onChange={onChange}
      onSaveSection={onSaveSection}
      dirty={{ [SECTION_ID]: dirty }}
      busy={isLoading || mutation.isPending}
      error={error}
    />
  );
}

function extractErrorMessage(err: unknown): string {
  if (!err) return "";
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string };
    if (first?.msg) return first.msg;
  }
  return e?.message ?? "Something went wrong saving your preferences.";
}
